<template>
<div>
  <div>
    <!-- Intro -->
    <div class="mb-lg">
      <p class="has-text-weight-bold has-text-info has-text-centered mb-md">Review Compatibility</p>
      <p v-if="proposalDetail !== null" class="is-size-7">
        Before you can submit your proposal, you must review compatibility with the merchants data requirements.
      </p>
    </div>
    <div v-if="loading">
      <loading />
    </div>
    <!-- Results -->
    <div v-if="!loading" class="mb-lg">
      <div class="columns is-multiline">
        <div class="column">
          <div class="has-background-light rounded-lg pa-sm has-text-centered">
            <p v-if="!loadingCompatibility" class="is-size-5 has-text-weight-bold">
              <span class="has-text-success">{{ proposalDetail.item_count }}</span>
            </p>
            <p v-else>Loading...</p>
            <p class="has-text-grey is-size-7 mb-xxs">Items Imported</p>
          </div>
        </div>
        <div class="column is-half">
          <div class="has-background-light rounded-lg pa-sm has-text-centered">
            <p v-if="!loadingCompatibility" class="is-size-5 has-text-weight-bold">
              <span v-if="proposalDetail.item_rejected_count > 0" class="has-text-warning">{{ proposalDetail.item_rejected_count }}</span>
              <span v-else class="has-text-success">{{ proposalDetail.item_rejected_count }}</span>
            </p>
            <p v-else>Loading...</p>
            <b-tooltip
              dashed
              label="Items are skipped if they are already being reviewed by your partner."
              position="is-bottom">
              <p class="has-text-grey is-size-7">Items Skipped</p>
            </b-tooltip>
          </div>
        </div>
        <div class="column is-half">
          <div class="has-background-light rounded-lg pa-sm has-text-centered">
            <p v-if="!loadingCompatibility" class="is-size-5 has-text-weight-bold">
              <span v-if="vendorRuleset && 'passed' in vendorRuleset && vendorRuleset.passed > 0" class="has-text-success">{{ vendorRuleset.passed }}</span>
              <span v-else-if="vendorRuleset && 'passed' in vendorRuleset && vendorRuleset.passed === 0" class="has-text-danger">0</span>
              <span v-else>--</span>
            </p>
            <p v-else>Loading...</p>
            <p class="has-text-grey is-size-7">Items Passing Validation</p>
          </div>
        </div>
        <div class="column is-half">
          <div class="has-background-light rounded-lg pa-sm has-text-centered">
            <p v-if="!loadingCompatibility" class="is-size-5 has-text-weight-bold">
              <span v-if="vendorRuleset && 'failed' in vendorRuleset && vendorRuleset.failed > 0" class="has-text-danger">{{ vendorRuleset.failed }}</span>
              <span v-else-if="vendorRuleset && 'failed' in vendorRuleset && vendorRuleset.failed === 0" class="has-text-success">0</span>
              <span v-else>--</span>
            </p>
            <p v-else>Loading...</p>
            <p class="has-text-grey is-size-7">Items Failing Validation</p>
          </div>
        </div>
      </div>
    </div>
    <!-- Messaging -->
    <div v-if="!loading">
      <!-- Has No Items (Rare) -->
      <b-message v-if="proposalDetail.status === 'draft' && proposalDetail.item_count === proposalDetail.item_rejected_count" type="is-warning" class="is-size-7">
        <div class="has-text-centered">
          <p class="has-text-weight-bold">No Items</p>
          <p>
            We're sorry, but no items are able to be imported at this time. It's likely
            that these items are already under review. If you have any questions, please don't
            hesitate to file a <a href="#" @click.prevent="$store.dispatch('supportRequest')" class="has-text-info">support request</a>.
          </p>
        </div>
      </b-message>
      <!-- Ready to Send -->
      <b-message v-else-if="proposalDetail.status === 'draft' && isPassing" type="is-success" class="is-size-7">
        <div class="has-text-centered">
          <p class="has-text-weight-bold">Compatibility Passed!</p>
          <p>All items are passing attribute validation checks.</p>
          <p>Your proposal is ready to send to {{ proposalDetail.connection.retailer.name }} for approval.</p>
        </div>
      </b-message>
      <!-- Failing -->
      <b-message v-else-if="proposalDetail.status === 'draft' && !isPassing" type="is-danger" class="is-size-7">
        <div class="mb-lg">
          <p class="has-text-weight-bold">Missing or invalid attributes need to be resolved before we can submit your proposal.</p>
          <p>Use the information below to update your product data, or let us help make the corrections for you.</p>
        </div>
        <div class="mb-lg">
          <FailingAttributes
            :proposal="proposalDetail"
            :ruleset="vendorRuleset"
            @inspect:rule="inspectRule"
          />
        </div>
        <div class="columns">
          <div class="column">
            <b-button type="is-danger" expanded outlined @click="showImportFeedModal = true">I want to fix it myself</b-button>
          </div>
          <div class="column">
            <b-button type="is-danger" expanded outlined @click="showImportHelpModal = true">Get help from {{ app.platform }}</b-button>
          </div>
        </div>
      </b-message>
      <!-- Already Sent -->
      <b-message v-else-if="proposalDetail.status === 'proposed'" type="is-success" class="is-size-7">
        <div class="has-text-centered">
          <p class="has-text-weight-bold">Awaiting Approval</p>
          <p>
            Your products have been sent to {{ proposalDetail.connection.retailer.name }} for review and approval. We'll
            notify you as soon as your submission is actioned. </p>
        </div>
      </b-message>
    </div>
    <!-- Navigate -->
    <div v-if="!loading" class="mt-xl">
      <div class="columns is-centered">
        <div class="column">
          <b-button
            type="is-light rounded-lg"
            size="is-medium"
            @click="goBack"
            expanded
            ><b-icon icon="arrow-left"></b-icon>
          </b-button>
        </div>
        <div class="column">
          <b-button
            v-if="proposal.status === 'draft'"
            type="is-primary rounded-lg"
            size="is-medium"
            expanded
            :disabled="saving || !isReadyToSubmit"
            :loading="saving"
            @click="submitProposal"
          >Submit for Approval</b-button>
          <b-button
            v-else
            type="is-primary rounded-lg"
            size="is-medium"
            expanded
            :loading="saving"
            @click="goForward"
          >Continue</b-button>
        </div>
      </div>
    </div>
  </div>
  <!-- Inspect Variants -->
  <b-modal
    has-modal-card
    :active.sync="showVariantsModal"
    :can-cancel="['escape', 'x']"
    @close="rule = null"
    ><VariantsModal :proposal="proposal" :rule="rule" />
  </b-modal>
  <!-- Import Feed Modal -->
  <b-modal
    has-modal-card
    :active.sync="showImportFeedModal"
    :can-cancel="['escape', 'x']"
    ><ImportFeedModal :proposal="proposalDetail" @feed:imported="refresh" />
  </b-modal>
  <!-- Support Task Modal -->
  <b-modal
    has-modal-card
    :active.sync="showImportHelpModal"
    :can-cancel="['escape', 'x']"
    ><ImportHelpModal :proposal="proposal" />
  </b-modal>
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import FailingAttributes from './FailingAttributes'
import ImportFeedModal from '@/components/Models/Proposal/Modals/ImportFeed'
import ImportHelpModal from '@/components/Models/Proposal/Modals/ImportHelp'
import VariantsModal from './VariantsModal'

export default {
  components: {
    FailingAttributes,
    ImportFeedModal,
    ImportHelpModal,
    VariantsModal
  },
  computed: {
    ...mapGetters(['app', 'context']),
    vendorRuleset () {
      if (!this.proposalDetail || this.rulesets.length === 0) return null
      const ruleset = this.rulesets.find(ruleset => {
        return ruleset.id === this.proposalDetail.department.brand_ruleset.id
      })
      return (typeof ruleset !== 'undefined') ? ruleset : null
    },
    isPassing () {
      if (this.vendorRuleset) {
        return this.vendorRuleset.failed === 0 && this.vendorRuleset.processing === 0
      }
      return false
    },
    isReadyToSubmit () {
      return (
        this.proposalDetail.status === 'draft' &&
        this.proposalDetail.item_count > 0 &&
        this.proposalDetail.item_failed_count === 0 &&
        this.proposalDetail.item_count - this.proposal.item_rejected_count !== 0
      )
    },
    loading () {
      return (this.loadingProposal || this.loadingCompatibility)
    }
  },
  data () {
    return {
      loadingProposal: true,
      loadingCompatibility: true,
      proposalDetail: null,
      rulesets: [],
      showDetails: false,
      saving: false,
      errors: {},
      rule: null,
      showImportFeedModal: false,
      showImportHelpModal: false,
      showVariantsModal: false
    }
  },
  methods: {
    goBack () {
      this.$emit('navigate', 'Progress')
    },
    goForward () {
      this.$emit('navigate', 'ProposalSubmitted')
    },
    loadProposal (silent = false) {
      if (!silent) this.loadingProposal = true
      const endpoint = this.context.route + '/proposals/' + this.proposal.id + '/'
      this.$http.get(endpoint).then(response => {
        this.proposalDetail = response.data
        this.loadingProposal = false
      })
    },
    loadCompatibility (silent = false) {
      if (!silent) this.loadingCompatibility = true
      const endpoint = this.context.route + '/proposals/' + this.proposal.id + '/rulesets-summary/'
      this.$http.get(endpoint).then(response => {
        this.rulesets = response.data
        this.loadingCompatibility = false
      })
    },
    inspectRule (rule) {
      this.rule = rule
      this.showVariantsModal = true
    },
    submitProposal () {
      this.saving = true
      const endpoint = this.context.route + '/proposals/' + this.proposal.id + '/'
      const body = {
        status: 'proposed'
      }
      this.$http.patch(endpoint, body).then(response => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Proposal submitted!' })
        this.$emit('navigate', 'ProposalSubmitted')
      })
    },
    archiveProposal () {
      this.saving = true
      const endpoint = this.context.route + '/proposals/' + this.proposal.id + '/'
      const body = {
        status: 'archived'
      }
      this.$http.patch(endpoint, body).then(response => {
        this.$buefy.toast.open({ type: 'is-info', message: 'Proposal archived!' })
        this.$router.push({ name: 'onboarding.tasks', params: { connection: this.connection.id } })
      })
    },
    refresh () {
      this.loadProposal()
      this.loadCompatibility()
    }
  },
  mounted () {
    this.loadProposal()
    this.loadCompatibility()
  },
  props: {
    connection: {
      type: Object,
      required: true
    },
    task: {
      type: Object,
      required: true
    },
    feed: {
      type: Object,
      required: true
    },
    proposal: {
      type: Object
    }
  }
}
</script>
